import type { LucideIcon } from 'lucide-react'
import { Loader } from 'lucide-react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon
  isLoading?: boolean
  variant?: ButtonVariant
  children?: React.ReactNode
}

// Mapping displayName → { hover, loading }
const ICON_ANIMATIONS: Record<string, { hover: string; loading: string }> = {
  RefreshCw:  { hover: 'group-hover:animate-spin-once',   loading: 'animate-spin-loop' },
  RotateCcw:  { hover: 'group-hover:animate-spin-once',   loading: 'animate-spin-loop' },
  X:          { hover: 'group-hover:animate-rotate-snap', loading: 'animate-rotate-snap-loop' },
  Trash2:     { hover: 'group-hover:animate-shake',       loading: 'animate-shake-loop' },
  Play:       { hover: 'group-hover:animate-nudge',       loading: 'animate-nudge-loop' },
  Send:       { hover: 'group-hover:animate-nudge',       loading: 'animate-nudge-loop' },
  Zap:        { hover: 'group-hover:animate-nudge',       loading: 'animate-nudge-loop' },
  Download:   { hover: 'group-hover:animate-bounce-down', loading: 'animate-bounce-down-loop' },
  Plus:       { hover: 'group-hover:animate-rotate-snap', loading: 'animate-spin-loop' },
  Save:       { hover: 'group-hover:animate-pulse-scale', loading: 'animate-pulse-scale-loop' },
  Check:      { hover: 'group-hover:animate-pulse-scale', loading: 'animate-pulse-scale-loop' },
  Loader:     { hover: 'animate-spin-loop',               loading: 'animate-spin-loop' },
}

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary:   'bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white hover:opacity-90 disabled:opacity-50',
  secondary: 'border border-[#E2E4ED] text-[#555A73] hover:text-[#1B2559] hover:bg-[#F4F5FA] disabled:opacity-50',
  ghost:     'text-[#8B8FA7] hover:text-[#1B2559] hover:bg-[#F4F5FA] disabled:opacity-40',
}

const VARIANT_ICON_OPACITY: Record<ButtonVariant, string> = {
  primary:   '',
  secondary: 'opacity-80',
  ghost:     'opacity-60',
}

export function Button({
  icon: Icon,
  isLoading = false,
  variant = 'primary',
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const iconName = Icon?.displayName ?? ''
  const anim = ICON_ANIMATIONS[iconName]

  // En loading sans animation mappée → remplacer par Loader
  const DisplayIcon = isLoading && !anim ? Loader : Icon

  const iconAnimClass = isLoading
    ? (anim?.loading ?? (DisplayIcon === Loader ? 'animate-spin-loop' : ''))
    : (anim?.hover ?? '')

  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={[
        'group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all',
        VARIANT_STYLES[variant],
        className,
      ].join(' ')}
    >
      {DisplayIcon && (
        <span className={['shrink-0', VARIANT_ICON_OPACITY[variant]].filter(Boolean).join(' ')}>
          <DisplayIcon
            className={['w-4 h-4', iconAnimClass].filter(Boolean).join(' ')}
            strokeWidth={1.5}
          />
        </span>
      )}
      {children}
    </button>
  )
}
